import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Menu, ShoppingBag, User, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LINKS = [
  { to: "/collections", label: "Collections", testid: "nav-collections-link" },
  { to: "/lookbook", label: "Lookbook", testid: "nav-lookbook-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (accountOpen && user) {
      setOrders(null);
      api
        .get("/orders/mine")
        .then((res) => setOrders(res.data))
        .catch(() => setOrders([]));
    }
  }, [accountOpen, user]);

  const onAccountClick = () => {
    if (user) {
      setAccountOpen((o) => !o);
    } else {
      navigate("/auth");
    }
  };

  const signOut = async () => {
    await logout();
    setAccountOpen(false);
    toast.success("Déconnecté.");
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 border-b border-white/5 bg-ink/70 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-[1700px] items-center justify-between px-5 sm:px-8 lg:px-16">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3 text-zinc-100">
            <img
              src="/logo-wordmark.png"
              alt="malgré les difficultés"
              className="h-6 w-auto mix-blend-screen sm:h-7"
            />
          </Link>

          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `group relative font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
                    isActive ? "text-zinc-100" : "text-zinc-500 hover:text-zinc-200"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-px bg-white transition-all duration-500 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                data-testid="account-button"
                onClick={onAccountClick}
                className="flex h-11 w-11 items-center justify-center border border-white/10 text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white"
                aria-label="Account"
              >
                {user && user.picture ? (
                  <img src={user.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </button>
              <AnimatePresence>
                {accountOpen && user && (
                  <motion.div
                    data-testid="account-dropdown"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.25 }}
                    className="absolute right-0 top-14 w-80 border border-white/10 bg-coal p-5"
                  >
                    <p className="font-display text-sm font-semibold uppercase tracking-wide">{user.name}</p>
                    <p className="mt-1 truncate font-mono text-[10px] text-zinc-500">{user.email}</p>

                    <div className="mt-5 border-t border-white/10 pt-4">
                      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">
                        Mes commandes
                      </p>
                      {orders === null ? (
                        <p className="mt-3 font-mono text-[10px] text-zinc-600">Chargement…</p>
                      ) : orders.length === 0 ? (
                        <p className="mt-3 font-mono text-[10px] text-zinc-600" data-testid="orders-empty">
                          Aucune commande pour l’instant.
                        </p>
                      ) : (
                        <ul className="mt-3 max-h-48 space-y-3 overflow-y-auto pr-1" data-testid="orders-list">
                          {orders.map((o) => (
                            <li key={o.id} className="border border-white/10 p-3" data-testid={`order-${o.id.slice(0, 8)}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] tracking-wider text-zinc-300">
                                  #{o.id.slice(0, 8).toUpperCase()}
                                </span>
                                <span
                                  className={`px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] ${
                                    o.status === "paid"
                                      ? "bg-white text-ink"
                                      : "border border-white/20 text-zinc-400"
                                  }`}
                                >
                                  {o.status === "paid" ? "Payée" : "En attente"}
                                </span>
                              </div>
                              <p className="mt-2 truncate font-mono text-[9px] text-zinc-500">
                                {o.items?.map((i) => `${i.name} (${i.size})`).join(", ")}
                              </p>
                              <div className="mt-2 flex items-center justify-between font-mono text-[9px] text-zinc-500">
                                <span>
                                  {o.created_at
                                    ? new Date(o.created_at).toLocaleDateString("fr-FR")
                                    : ""}
                                </span>
                                <span className="text-zinc-200">€{o.total?.toFixed(0)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="mt-4 space-y-2">
                      <Link
                        to="/compte"
                        data-testid="account-page-link"
                        className="flex h-10 w-full items-center justify-center border border-white/15 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white"
                      >
                        Mon compte
                      </Link>
                      {user.role === "admin" && (
                        <Link
                          to="/admin"
                          data-testid="admin-dashboard-link"
                          className="flex h-10 w-full items-center justify-center bg-white font-mono text-[9px] uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-zinc-300"
                        >
                          Dashboard
                        </Link>
                      )}
                    </div>
                    <button
                      data-testid="account-logout-button"
                      onClick={signOut}
                      className="mt-2 flex h-10 w-full items-center justify-center gap-2 border border-white/15 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white"
                    >
                      <LogOut className="h-3 w-3" /> Se déconnecter
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              data-testid="cart-toggle-button"
              onClick={() => setIsOpen(true)}
              className="group relative flex h-11 w-11 items-center justify-center border border-white/10 text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-4 w-4" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center bg-white font-mono text-[10px] font-medium text-ink"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="flex h-11 w-11 items-center justify-center border border-white/10 text-zinc-300 transition-colors duration-300 hover:border-white hover:text-white md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 flex flex-col bg-ink"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <img
                src="/logo-wordmark.png"
                alt="malgré les difficultés"
                className="h-6 w-auto mix-blend-screen"
              />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center border border-white/10 text-zinc-300"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[
                { to: "/", label: "Home", testid: "mobile-nav-home" },
                ...LINKS,
                { to: "/auth", label: user ? "Compte" : "Connexion", testid: "mobile-nav-account" },
              ].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.testid}
                    className="font-blackletter text-5xl leading-[1.1] text-zinc-100 transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">
              Paris — Est. MMXXI
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
